import {z} from 'zod';

import {EntityResourceIds} from '@dms/api/shared';

export const CustomNoticeColorSchema = z.enum([
  'neutral',
  'blue',
  'teal',
  'green',
  'orange',
  'yellow',
  'magenta',
  'purple',
  'red',
]);
export type CustomNoticeColor = z.infer<typeof CustomNoticeColorSchema>;

export const CustomNoticeIconSchema = z.enum([
  'information',
  'warning',
  'danger',
  'help',
  'checkmark',
  'remove',
  'notification',
  'person',
  'vehicle',
]);
export type CustomNoticeIcon = z.infer<typeof CustomNoticeIconSchema>;

export const CustomNoticeResponseBodySchema = z.object({
  id: z.string(),
  text: z.string(),
  color: CustomNoticeColorSchema,
  icon: CustomNoticeIconSchema,
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type CustomNoticeResponseBody = z.infer<typeof CustomNoticeResponseBodySchema>;

export const CustomNoticeRequestBodySchema = z.object({
  text: z.string().trim().min(1),
  color: CustomNoticeColorSchema,
  icon: CustomNoticeIconSchema,
});
export type CustomNoticeRequestBody = z.infer<typeof CustomNoticeRequestBodySchema>;

export const CustomNoticeRelationSchema = z.enum([
  'own',
  'ownership',
  'customer',
  'vehicle',
  'interest',
  'businessCase',
  'serviceCase',
  'tireOrder',
  'rentalOrder',
  'workshopInterest',
  'directSale',
]);
export type CustomNoticeRelation = z.infer<typeof CustomNoticeRelationSchema>;

export const CustomNoticeSourceSchema = z.object({
  resourceId: z.nativeEnum(EntityResourceIds),
  // recordId is used to build navigation paths, so it must stay a plain UUID
  recordId: z.string().uuid(),
  label: z.string().nullable(),
});
export type CustomNoticeSource = z.infer<typeof CustomNoticeSourceSchema>;

export const RelatedCustomNoticeResponseBodySchema = CustomNoticeResponseBodySchema.extend({
  relation: CustomNoticeRelationSchema,
  roles: z.array(z.string()),
  source: CustomNoticeSourceSchema,
});
export type RelatedCustomNoticeResponseBody = z.infer<typeof RelatedCustomNoticeResponseBodySchema>;

const CustomNoticeRecordArgSchema = z.object({
  resourceId: z.nativeEnum(EntityResourceIds),
  recordId: z.string(),
});

export const CustomNoticeListItemSchema = CustomNoticeResponseBodySchema.extend({
  canUpdate: z.boolean(),
  canDelete: z.boolean(),
});
export type CustomNoticeListItem = z.infer<typeof CustomNoticeListItemSchema>;

export const GetCustomNoticesApiResponseSchema = z.array(CustomNoticeListItemSchema);
export const GetCustomNoticesApiArgSchema = CustomNoticeRecordArgSchema;
export type GetCustomNoticesApiResponse = z.infer<typeof GetCustomNoticesApiResponseSchema>;
export type GetCustomNoticesApiArg = z.infer<typeof GetCustomNoticesApiArgSchema>;

export const GetCustomNoticePermissionsApiResponseSchema = z.object({canAdd: z.boolean()});
export const GetCustomNoticePermissionsApiArgSchema = CustomNoticeRecordArgSchema;
export type GetCustomNoticePermissionsApiResponse = z.infer<
  typeof GetCustomNoticePermissionsApiResponseSchema
>;
export type GetCustomNoticePermissionsApiArg = z.infer<
  typeof GetCustomNoticePermissionsApiArgSchema
>;

export const GetRelatedCustomNoticesApiResponseSchema = z.array(
  RelatedCustomNoticeResponseBodySchema
);
export type GetRelatedCustomNoticesApiResponse = z.infer<
  typeof GetRelatedCustomNoticesApiResponseSchema
>;

export const RelatedCustomNoticesResourceIdSchema = z.union([
  z.literal(EntityResourceIds.customer),
  z.literal(EntityResourceIds.vehicle),
  z.literal(EntityResourceIds.businessCase),
  z.literal(EntityResourceIds.interest),
  z.literal(EntityResourceIds.rentalOrder),
  z.literal(EntityResourceIds.serviceCase),
  z.literal(EntityResourceIds.tireOrder),
  z.literal(EntityResourceIds.workshopInterest),
  z.literal(EntityResourceIds.directSale),
]);
export type RelatedCustomNoticesResourceId = z.infer<typeof RelatedCustomNoticesResourceIdSchema>;

export const GetRelatedCustomNoticesApiArgSchema = CustomNoticeRecordArgSchema.extend({
  resourceId: RelatedCustomNoticesResourceIdSchema,
});
export type GetRelatedCustomNoticesApiArg = z.infer<typeof GetRelatedCustomNoticesApiArgSchema>;

export const CreateCustomNoticeApiResponseSchema = z.void();
export const CreateCustomNoticeApiArgSchema = CustomNoticeRecordArgSchema.extend({
  body: CustomNoticeRequestBodySchema,
});
export type CreateCustomNoticeApiResponse = z.infer<typeof CreateCustomNoticeApiResponseSchema>;
export type CreateCustomNoticeApiArg = z.infer<typeof CreateCustomNoticeApiArgSchema>;

export const UpdateCustomNoticeApiResponseSchema = z.void();
export const UpdateCustomNoticeApiArgSchema = CustomNoticeRecordArgSchema.extend({
  customNoticeId: z.string(),
  body: CustomNoticeRequestBodySchema,
});
export type UpdateCustomNoticeApiResponse = z.infer<typeof UpdateCustomNoticeApiResponseSchema>;
export type UpdateCustomNoticeApiArg = z.infer<typeof UpdateCustomNoticeApiArgSchema>;

export const DeleteCustomNoticeApiResponseSchema = z.void();
export const DeleteCustomNoticeApiArgSchema = CustomNoticeRecordArgSchema.extend({
  customNoticeId: z.string(),
});
export type DeleteCustomNoticeApiResponse = z.infer<typeof DeleteCustomNoticeApiResponseSchema>;
export type DeleteCustomNoticeApiArg = z.infer<typeof DeleteCustomNoticeApiArgSchema>;
